/* ─────────────────────────────────────────────────────────────────────────
   ganttUtils.js  –  pure helpers, no React, no MUI
   ───────────────────────────────────────────────────────────────────────── */

import { getSiStatus, getSiStatusColor } from "../../../MCS3/Schedule4D/siUtils";

/* Task-type metadata ──────────────────────────────────────────────────── */
export const TYPE_META = {
  engineering: { label: "EMS", color: "#1565c0", icon: "⚙" },
  procurement:  { label: "PMS", color: "#6a1b9a", icon: "📦" },
  fabrication:  { label: "CMS", color: "#e65100", icon: "🔧" },
  itp:          { label: "ITP", color: "#2e7d32", icon: "✔" },
};

export const TYPE_BY_FOUR_D = {
  EMS: TYPE_META.engineering,
  PMS: TYPE_META.procurement,
  CMS: TYPE_META.fabrication,
  ITP: TYPE_META.itp,
};

/* Resolve best "end" date for SI status calc ─────────────────────────── */
export const resolvePlannedEnd = (row) =>
  row?.revisedEnd || row?.plannedEnd || row?.endDate || row?.targetDate || row?.baselineEnd;

/* Enrich a row with siStatus + color ─────────────────────────────────── */
export const enrichRow = (row) => {
  const siStatus =
    row._siStatus ??
    getSiStatus({ si: row.si, plannedEnd: resolvePlannedEnd(row) });
  return { ...row, _siStatus: siStatus, _barColor: getSiStatusColor(siStatus) };
};

/* Date utilities ──────────────────────────────────────────────────────── */
export const parseDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const d = new Date(String(v).slice(0, 10));
  return isNaN(d.getTime()) ? null : d;
};

export const toISO = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

export const fmtDate = (v) => {
  const d = parseDate(v);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

/* Compute the pixel x-coordinate of a date within the chart ──────────── */
export const dateToX = (date, rangeStart, totalMs, totalPx) => {
  const ms = date.getTime() - rangeStart.getTime();
  return Math.max(0, (ms / totalMs) * totalPx);
};

/* Compute chart date range from rows ─────────────────────────────────── */
export const computeRange = (rows, bufferDays = 30) => {
  const dates = [];
  rows.forEach((r) => {
    const s = parseDate(r.plannedStart);
    const e = parseDate(r.plannedEnd);
    if (s) dates.push(s);
    if (e) dates.push(e);
  });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (!dates.length)
    return { start: new Date(today.getFullYear(), today.getMonth() - 1, 1), end: new Date(today.getFullYear(), today.getMonth() + 4, 1) };

  const min = new Date(Math.min(...dates.map((d) => d.getTime())));
  const max = new Date(Math.max(...dates.map((d) => d.getTime())));
  min.setDate(min.getDate() - bufferDays);
  max.setDate(max.getDate() + bufferDays);
  return { start: min, end: max };
};

/* Build month tick marks from a date range ───────────────────────────── */
export const buildMonths = (start, end) => {
  const months = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cur <= end) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
};
